import React, { useImperativeHandle, forwardRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Form, Input, Drawer, Space, Button, App } from 'antd'
import { useSearchParams } from 'react-router-dom'
import { addKnowledgeItem, modifyKnowledgeItem } from '@/api/dictionary'

interface EditDictItemProps {
  onSuccess?: (isEdit: boolean) => void
  isAE?: boolean
}

const EditDictItem = ({ onSuccess,isAE }: EditDictItemProps, ref: any) => {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { message } = App.useApp()
  const [visible, setVisible] = useState(false)
  const [record, setRecord] = useState<any>({})
  const [title, setTitle] = useState('')
  const [form] = Form.useForm()
  const dictCode = searchParams.get('dictCode')
  const close = () => {
    setRecord({})
    form.resetFields()
    setVisible(false)
  }
  useImperativeHandle(ref, () => ({
    open: (record: any) => {
      setRecord(record)
      form.setFieldsValue(record)
      if (record.id) {
        setTitle(t('ikg.edit'))
      } else {
        setTitle(t('dict.addWord'))
      }
      setVisible(true)
    },
  }))
  const onFinish = async (values: any) => {
    try {
      const isEdit = !!values.id
      if (!isAE) {
        values.name = values.normalName
      }
      if (isEdit) {
        delete values.directoryId
        await modifyKnowledgeItem(values)
      } else {
        values.id = values.directoryId
        delete values.directoryId
        await addKnowledgeItem(values)
      }
      close()
      message.success(t('ikg.success', { action: title }))
      typeof onSuccess === 'function' && onSuccess(isEdit)
    } catch (error) {
      close()
    }
  }
  const ctcaeFormItem = () => {
    return (
      <>
        <Form.Item label={t('dict.code')} name="code">
          <Input placeholder={t('ikg.input', { name: t('dict.code') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt1')}
          name="name"
          rules={[
            {
              required: true,
              message: t('ikg.input', { name: t('knowledge.txt1') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt1') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt2')}
          name="definition"
          rules={[
            {
              required: true,
              message: t('ikg.input', { name: t('knowledge.txt2') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt2') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt3')}
          name="navigationalNote"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt3') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt3') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt4')}
          name="grade1"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt4') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt4') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt5')}
          name="grade2"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt5') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt5') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt6')}
          name="grade3"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt6') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt6') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt7')}
          name="grade4"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt7') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt7') })} />
        </Form.Item>
        <Form.Item
          label={t('knowledge.txt8')}
          name="grade5"
          rules={[
            {
              required: false,
              message: t('ikg.input', { name: t('knowledge.txt8') }),
            },
          ]}
        >
          <Input placeholder={t('ikg.input', { name: t('knowledge.txt8') })} />
        </Form.Item>
      </>
    )
  }

  const ypkcFormItem = () => (
    <>
      <Form.Item
        label={t('knowledge.txt10')}
        name="normalName"
        rules={[
          {
            required: true,
            message: t('ikg.input', { name: t('knowledge.txt10') }), // 通用名
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt10') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt11')}
        name="tradeName"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt11') }), // 商品名
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt11') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt12')}
        name="englishName"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt12') }), // 英文名
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt12') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt13')}
        name="activeIngredient"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt13') }), // 有效成分
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt13') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt14')}
        name="manufacturer"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt14') }), // 生产厂家
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt14') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt15')}
        name="specs"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt15') }), // 规格
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt15') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt16')}
        name="dosage"
        rules={[
          {
            required: false,
            message: t('ikg.input', { name: t('knowledge.txt16') }), // 剂型
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt16') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt17')}
        name="approvalNo"
        rules={[
          {
            required: true,
            message: t('ikg.input', { name: t('knowledge.txt17') }), // 国批文号
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt17') })} />
      </Form.Item>
      <Form.Item
        label={t('knowledge.txt18')}
        name="drugStandardCode"
        rules={[
          {
            required: true,
            message: t('ikg.input', { name: t('knowledge.txt18') }), // 药品本位码
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt18') })} />
      </Form.Item>
      <Form.Item hidden name="code">
        <Input />
      </Form.Item>
      {/* <Form.Item
        label={t('knowledge.txt19')}
        name="definition"
        rules={[
          {
            required: true,
            message: t('ikg.input', { name: t('knowledge.txt19') }), // 附件
          },
        ]}
      >
        <Input placeholder={t('ikg.input', { name: t('knowledge.txt19') })} />
      </Form.Item> */}
    </>
  )
  return (
    <Drawer
      title={title}
      width={460}
      open={visible}
      onClose={close}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Space>
            <Button onClick={close}>{t('ikg.cancel')}</Button>
            <Button type="primary" onClick={() => form.submit()}>
              {t('ikg.sure')}
            </Button>
          </Space>
        </div>
      }
    >
      <Form form={form} onFinish={onFinish} layout="vertical" autoComplete="off">
        {isAE ? ctcaeFormItem() : ypkcFormItem()}
        {record?.directoryId && (
          <Form.Item hidden name="directoryId">
            <Input />
          </Form.Item>
        )}
        {record?.id && (
          <Form.Item hidden name="id">
            <Input />
          </Form.Item>
        )}
        {record?.dictId && (
          <Form.Item hidden name="dictId">
            <Input />
          </Form.Item>
        )}
        <Form.Item hidden name="type">
          <Input />
        </Form.Item>
      </Form>
    </Drawer>
  )
}

export default forwardRef(EditDictItem)
